import React, { useEffect, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    makeStyles,
    TextField,
    Typography,
} from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { useTranslation } from 'react-i18next'
import { createTenant } from '../../redux/actions/dashboard'
import { Tenant } from '../../shared/types/tenant'
import { generatePassword, validateTenantPass } from './SetPasswordDialog'

const isValidLogin = (login: string) => Boolean(login.trim()) && !/\s/.test(login.trim())

const helperProps = { style: { marginLeft: 0, marginRight: 0 } }

const useStyles = makeStyles((theme) => ({
    field: {
        marginTop: theme.spacing(1),
        width: 400,
        display: 'block',
    },
    subtitle: {
        marginTop: theme.spacing(2),
        fontWeight: 600,
    },
    hint: {
        color: theme.palette.text.secondary,
    },
}))

interface Props {
    open: boolean
    onClose: () => void
    onCreated: (tenant: Tenant) => void
    onError: (msg: string) => void
}

export default function TenantDialog({ open, onClose, onCreated, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const [name, setName] = useState('')
    const [contact, setContact] = useState('')
    const [login, setLogin] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [touched, setTouched] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) return
        setName('')
        setContact('')
        setLogin('')
        setPassword('')
        setShowPassword(false)
        setTouched(false)
    }, [open])

    const withAdmin = login.trim().length > 0
    const nameError = touched && !name.trim()
    const loginError = withAdmin ? !isValidLogin(login) : password.length > 0
    const passwordError = password.length > 0 && !validateTenantPass(password)
    const adminOk = !withAdmin ? password.length === 0 : isValidLogin(login) && validateTenantPass(password)
    const canSave = Boolean(name.trim()) && adminOk && !saving

    const close = () => {
        if (!saving) onClose()
    }

    const handleSave = async () => {
        setTouched(true)
        if (!canSave) return
        setSaving(true)
        try {
            const tenant = await createTenant(
                name.trim(),
                contact.trim(),
                withAdmin ? login.trim() : undefined,
                withAdmin ? password : undefined
            )
            onCreated(tenant)
        } catch {
            onError(t('dashboard.errorCreatingTenant'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onClose={close}>
            <DialogTitle>{t('dashboard.addTenant')}</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    required
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('dashboard.tenantName')}
                    value={name}
                    inputProps={{ maxLength: 256 }}
                    error={nameError}
                    FormHelperTextProps={helperProps}
                    helperText={nameError ? t('common.required') : ' '}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('dashboard.contact')}
                    value={contact}
                    inputProps={{ maxLength: 1000 }}
                    FormHelperTextProps={helperProps}
                    helperText=" "
                    onChange={(e) => setContact(e.target.value)}
                />

                <Typography variant="subtitle2" className={classes.subtitle}>
                    {t('dashboard.firstAdmin')}
                </Typography>
                <Typography variant="caption" className={classes.hint}>
                    {t('dashboard.firstAdminHint')}
                </Typography>
                <TextField
                    fullWidth
                    className={classes.field}
                    variant="filled"
                    label={t('dashboard.login')}
                    value={login}
                    inputProps={{ maxLength: 256 }}
                    error={loginError}
                    FormHelperTextProps={helperProps}
                    helperText={loginError ? t('dashboard.invalidLogin') : ' '}
                    onChange={(e) => setLogin(e.target.value)}
                />
                <TextField
                    fullWidth
                    required={withAdmin}
                    className={classes.field}
                    variant="filled"
                    label={t('common.newPassword')}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    error={passwordError}
                    FormHelperTextProps={helperProps}
                    helperText={withAdmin || password.length > 0 ? t('dashboard.passwordRules') : ' '}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setPassword(generatePassword())
                        setShowPassword(true)
                    }}
                    disabled={saving}
                >
                    {t('dashboard.generatePassword')}
                </Button>
                <div style={{ flex: 1 }} />
                <Button onClick={close} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!canSave}>
                    {saving ? <CircularProgress size={20} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
